"use client";
import { CustomButton, FlexCenter, FlexCol, TextField } from "@/components";
import { cn, convertToJalaliDateTime } from "@/lib";
import React, { useEffect, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { inquiryValiditySchema } from "@/validations";
import { BdMagnifer } from "solar-icon-react/bd";
import { BoCloseCircle, BoVerifiedCheck } from "solar-icon-react/bo";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { IRawInquiryValidityResData } from "@/interfaces";

interface IProps {
  inquiryValidityData: IRawInquiryValidityResData | null;
}

const CheckInquiryValiditySection = ({ inquiryValidityData }: IProps) => {
  const {
    control,
    formState: { errors: formError },
    getValues,
    setValue,
  } = useForm<{ code: string }>({
    resolver: zodResolver(inquiryValiditySchema),
    defaultValues: {
      code: "",
    },
  });
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const params = new URLSearchParams(searchParams?.toString() || "");
  const resultsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const code = searchParams?.get("code");
    if (code) {
      setValue("code", code);

      setTimeout(() => {
        resultsRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    } else {
      setValue("code", "");
    }
  }, [searchParams, setValue]);

  const onSubmit = () => {
    params.set("code", getValues().code);
    router.push(`${pathname}?${params.toString()}`, { scroll: true });
  };

  let date = inquiryValidityData?.created_at
    ? convertToJalaliDateTime(inquiryValidityData?.created_at)
    : "نامشخص";
  date = date.split(" ")[0];
  return (
    <section className="relative z-30 mt-[-150px] mb-14 min-h-[100vh] md:m-4 md:mt-[-180px] lg:mx-0 lg:mt-[-200px] xl:mb-20">
      <div className="mx-auto size-full max-w-[700px] rounded-xl bg-white lg:max-w-[900px] xl:max-w-full">
        <div className="relative">
          <div className="px-4 py-8 lg:px-8 lg:py-18">
            <FlexCol>
              <form className="mx-auto w-full max-w-[600px]">
                <FlexCenter
                  gap={4}
                  className="mx-auto mb-8 w-full flex-col items-center justify-center sm:flex-row"
                >
                  <Controller
                    name={"code"}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        hasShadow
                        aria-label="code"
                        placeholder="کد استعلام را جهت بررسی اصالت وارد نمایید."
                        inputClassName={cn("tracking-widest h-10 text-en")}
                        error={formError.code?.message}
                      />
                    )}
                  />
                  <CustomButton
                    id="check-inquiry-validity"
                    onClick={onSubmit}
                    className={cn(
                      "bg-primary-main mx-auto w-fit px-8! py-[10px]! text-white",
                    )}
                    icon={<BdMagnifer size={20} />}
                    iconPosition="end"
                  >
                    بررسی
                  </CustomButton>
                </FlexCenter>
              </form>
              <p className="text-center text-sm leading-6 font-bold">
                در این بخش می‌توانید اصالت گزارش‌های صادرشده از سامانه نوین وان
                را بررسی کنید.
                <br />
                کافیست کد استعلام درج‌شده در گزارش خود را وارد کنید تا وضعیت
                اعتبار آن نمایش داده شود.
              </p>
            </FlexCol>
          </div>
          {inquiryValidityData && (
            <div>
              <svg
                className="mt-6 h-[4px] w-full text-gray-300 lg:mt-[30px]"
                xmlns="http://www.w3.org/2000/svg"
                preserveAspectRatio="none"
              >
                <line
                  x1="0"
                  y1="2"
                  x2="100%"
                  y2="2"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeDasharray="15 14" // ← 20 طول dash ، 12 فاصله بین dash
                />
              </svg>
              <div
                className={cn(
                  "absolute -bottom-10 flex w-full justify-between",
                )}
              >
                <div className="bg-main-body hidden rounded-full md:-mr-12 md:block md:h-[70px] md:w-[70px] lg:h-[90px] lg:w-[90px]"></div>

                <div className="bg-main-body hidden rounded-full md:-ml-12 md:block md:h-[70px] md:w-[70px] lg:h-[90px] lg:w-[90px]"></div>
              </div>
            </div>
          )}
        </div>
        {inquiryValidityData && (
          <div ref={resultsRef} className="px-4 py-10 md:px-0">
            {inquiryValidityData.status === "successful" ? (
              <div className="mx-auto max-w-[500] rounded-lg border border-emerald-400 bg-emerald-100/40 p-4 sm:p-6">
                <FlexCol gap={6}>
                  <div className="flex items-center gap-4">
                    <BoVerifiedCheck
                      size={40}
                      className={cn("text-emerald-600!")}
                    />
                    <span className="text-base font-bold text-emerald-600 sm:text-lg md:text-xl">
                      گزارش معتبر است
                    </span>
                  </div>
                  <span className="text-base font-bold md:text-lg">
                    این گزارش توسط سامانه نوین وان صادر شده است.
                  </span>
                  <ul className="list-disc pr-5">
                    <li className="text-sm font-semibold sm:text-base lg:text-lg">
                      <span className="mx-1">نوع استعلام :</span>
                      <span className="mx-1">
                        {inquiryValidityData.inquery.name}
                      </span>
                    </li>
                    <li className="text-sm font-semibold sm:text-base lg:text-lg">
                      <span className="mx-1">تاریخ :</span>
                      <span className="mx-1" dir="ltr">
                        {date}
                      </span>
                    </li>
                    <li className="text-sm font-semibold sm:text-base lg:text-lg">
                      <span className="mx-1">وضعیت :</span>
                      <span className="mx-1">معتبر</span>
                    </li>
                    <li className="text-sm font-semibold sm:text-base lg:text-lg">
                      <span className="mx-1">شناسه پیگیری :</span>
                      <span className="text-en mx-1 font-medium">
                        {inquiryValidityData.code}
                      </span>
                    </li>
                  </ul>
                </FlexCol>
              </div>
            ) : (
              <div className="mx-auto max-w-[500] rounded-lg border border-red-400 bg-red-100/40 p-6">
                <FlexCol gap={6}>
                  <div className="flex items-center gap-4">
                    <BoCloseCircle size={40} className={cn("text-red-700!")} />
                    <span className="text-base font-bold text-red-700 sm:text-lg md:text-xl">
                      گزارش نامعتبر است
                    </span>
                  </div>
                  <p className="text-base leading-6 font-semibold md:text-lg md:leading-8">
                    کد واردشده معتبر نیست یا در سامانه یافت نشد.
                    <br />
                    لطفاً از صحت کد استعلام خود اطمینان حاصل کرده و مجدداً تلاش
                    کنید.
                  </p>
                </FlexCol>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default CheckInquiryValiditySection;
